import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import { formatExamDate, examSchedule } from '../../utils/examDates';
import CompareOverlay from './CompareOverlay';
import CompareCards, { ChipList, LineList, Pill } from './CompareCards';

const GREEN = '#1D9E75';
const RED   = '#E24B4A';
const AMBER = '#F5A623';

// Workers are always free -- none of these three views is tier-gated, by design.

function Overlay({ eyebrowKey, titleKey, count, onClose, onClear, children }) {
  const { t } = useTranslation();
  return (
    <CompareOverlay
      eyebrow={t(eyebrowKey)}
      title={t(titleKey, { count })}
      onClose={onClose}
      onClear={count > 0 ? onClear : null}
      clearLabel={t('common.clearAll')}
      closeLabel={t('compare.closeAriaLabel')}
    >
      {children}
    </CompareOverlay>
  );
}

// ── Job postings the worker is weighing up. Everything needed is already on the loaded rows. ──
export function WorkerJobCompare({ jobs, onRemove, onClear, onClose }) {
  const { t } = useTranslation();
  const items = jobs.map(job => ({ id: job.id, title: job.job_title, subtitle: job.company_name, job }));
  const rows = [
    {
      key: 'pay', label: t('compare.worker.jobs.rows.pay'),
      render: ({ job }) => (job.salary_min || job.salary_max)
        ? <span style={{ color: AMBER, fontWeight: 700 }}>${job.salary_min}–${job.salary_max}/hr</span>
        : null,
    },
    { key: 'benefits', label: t('compare.worker.jobs.rows.benefits'), render: ({ job }) => <ChipList values={job.benefits || []} /> },
    { key: 'shift', label: t('compare.worker.jobs.rows.shift'), render: ({ job }) => job.shift },
    {
      key: 'location', label: t('compare.worker.jobs.rows.location'),
      render: ({ job }) => {
        const place = [job.city, job.state].filter(Boolean).join(', ');
        if (!place && !job.travel_required) return null;
        return <LineList values={[place, job.travel_required && t('worker.dashboard.jobs.travelRequired')].filter(Boolean)} />;
      },
    },
    { key: 'type', label: t('compare.worker.jobs.rows.jobType'), render: ({ job }) => [job.job_type, job.union_status].filter(Boolean).join(' · ') },
  ];
  return (
    <Overlay eyebrowKey="compare.worker.jobs.eyebrow" titleKey="compare.worker.jobs.title" count={jobs.length} onClose={onClose} onClear={onClear}>
      <CompareCards items={items} rows={rows} onRemove={onRemove} />
    </Overlay>
  );
}

// ── Offers the worker has actually received: the latest job_offers row per application. ──
export function WorkerOfferCompare({ applications, onRemove, onClear, onClose }) {
  const { t } = useTranslation();
  const [offers, setOffers] = useState({});
  const [loading, setLoading] = useState(true);

  const key = applications.map(a => a.id).sort().join(',');
  useEffect(() => {
    if (applications.length === 0) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase.from('job_offers')
        .select('application_id, offered_pay, pay_schedule, start_date, benefits, status, created_at')
        .in('application_id', applications.map(a => a.id))
        .order('created_at', { ascending: false });
      if (cancelled) return;
      const latest = {};
      (data || []).forEach(o => { if (!latest[o.application_id]) latest[o.application_id] = o; });
      setOffers(latest);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = applications.map(app => ({
    id: app.id,
    title: app.job?.job_title || t('compare.unnamed'),
    subtitle: app.employer?.company_name || app.job?.company_name,
  }));
  const o = item => offers[item.id] || null;
  const rows = [
    {
      key: 'status', label: t('compare.worker.offers.rows.status'),
      render: item => {
        const s = o(item)?.status;
        if (!s) return null;
        const color = s === 'accepted' ? GREEN : s === 'declined' ? RED : AMBER;
        return <Pill color={color}>{t(`compare.worker.offers.status.${s}`, { defaultValue: s })}</Pill>;
      },
    },
    {
      key: 'pay', label: t('compare.worker.offers.rows.pay'),
      render: item => o(item)?.offered_pay ? (
        <span>
          <span style={{ color: AMBER, fontWeight: 700 }}>{o(item).offered_pay}</span>
          {o(item).pay_schedule && <span style={{ color: 'rgba(255,255,255,0.5)' }}> · {o(item).pay_schedule}</span>}
        </span>
      ) : null,
    },
    { key: 'start', label: t('compare.worker.offers.rows.startDate'), render: item => formatExamDate(o(item)?.start_date) },
    { key: 'benefits', label: t('compare.worker.offers.rows.benefits'), render: item => <ChipList values={o(item)?.benefits || []} /> },
  ];
  return (
    <Overlay eyebrowKey="compare.worker.offers.eyebrow" titleKey="compare.worker.offers.title" count={applications.length} onClose={onClose} onClear={onClear}>
      <CompareCards items={items} rows={rows} loading={loading} onRemove={onRemove} />
    </Overlay>
  );
}

// ── Apprenticeship programs across unions. Exams come from the tab's already-loaded map; the
// pay ladder is the union's union_pay_scales, with the same legacy-directory fallback the public
// union page uses (a curated `unions` entry linked via union_account_id). ──
export function WorkerApprenticeshipCompare({ apprenticeships, examsByAppr, onRemove, onClear, onClose }) {
  const { t } = useTranslation();
  const [ladders, setLadders] = useState({});
  const [loading, setLoading] = useState(true);

  const key = apprenticeships.map(a => a.id).sort().join(',');
  useEffect(() => {
    if (apprenticeships.length === 0) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const unionIds = [...new Set(apprenticeships.map(a => a.union_id).filter(Boolean))];
      const [{ data: own }, { data: directory }] = await Promise.all([
        supabase.from('union_pay_scales').select('union_id, level, hourly_rate, sort_order').in('union_id', unionIds).order('sort_order'),
        supabase.from('unions').select('id, union_account_id').in('union_account_id', unionIds),
      ]);
      const byUnion = {};
      (own || []).forEach(r => { (byUnion[r.union_id] ||= []).push(r); });
      const fallbackIds = (directory || []).filter(u => !byUnion[u.union_account_id]?.length);
      if (fallbackIds.length) {
        const { data: curated } = await supabase.from('union_pay_scales').select('union_id, level, hourly_rate, sort_order')
          .in('union_id', fallbackIds.map(u => u.id)).order('sort_order');
        fallbackIds.forEach(u => { byUnion[u.union_account_id] = (curated || []).filter(r => r.union_id === u.id); });
      }
      if (cancelled) return;
      setLadders(byUnion);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = apprenticeships.map(a => ({ id: a.id, title: a.title, subtitle: a.union_name, appr: a }));
  const rows = [
    {
      key: 'ladder', label: t('compare.worker.apprenticeships.rows.payLadder'),
      render: ({ appr }) => {
        const ladder = (ladders[appr.union_id] || []).filter(r => r.hourly_rate != null);
        if (ladder.length) {
          return <LineList values={ladder.map(r => `${r.level}: $${Number(r.hourly_rate).toFixed(2)}/hr`)} />;
        }
        if (appr.hourly_rate_start) {
          return t('compare.worker.apprenticeships.startToJourneyman', {
            start: appr.hourly_rate_start, end: appr.hourly_rate_end || appr.hourly_rate_start,
          });
        }
        return null;
      },
    },
    { key: 'length', label: t('compare.worker.apprenticeships.rows.programLength'), render: ({ appr }) => appr.duration },
    {
      key: 'exams', label: t('compare.worker.apprenticeships.rows.examRequirements'),
      render: ({ appr }) => {
        const exams = examsByAppr[appr.id] || [];
        if (!exams.length) return t('compare.worker.apprenticeships.noExam');
        return (
          <LineList values={exams.map(ex => {
            const sched = examSchedule(ex);
            const when = sched.individual ? t('compare.worker.apprenticeships.scheduledIndividually')
              : sched.test_date ? t('compare.worker.apprenticeships.nextTest', { date: formatExamDate(sched.test_date) })
              : null;
            return when ? `${ex.name} — ${when}` : ex.name;
          })} />
        );
      },
    },
    {
      key: 'gi', label: t('compare.worker.apprenticeships.rows.giBill'),
      render: ({ appr }) => appr.gi_bill_eligible
        ? <Pill color={GREEN}>{t('compare.worker.apprenticeships.giEligible')}</Pill>
        : t('compare.worker.apprenticeships.giNotListed'),
    },
    {
      key: 'drug', label: t('compare.worker.apprenticeships.rows.drugScreening'),
      render: ({ appr }) => appr.drug_screening_required
        ? <Pill color="#FFFFFF">{t('marketing.trades.drugScreeningRequired')}</Pill>
        : t('compare.worker.apprenticeships.drugNotRequired'),
    },
  ];
  return (
    <Overlay eyebrowKey="compare.worker.apprenticeships.eyebrow" titleKey="compare.worker.apprenticeships.title" count={apprenticeships.length} onClose={onClose} onClear={onClear}>
      <CompareCards items={items} rows={rows} loading={loading} onRemove={onRemove} />
    </Overlay>
  );
}
